import React, { useContext } from 'react'
import { Grid } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { Field, FieldRenderProps } from 'react-final-form'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import { FormApi } from 'final-form'
import { objectGetKeys } from '../../../utils/ieCompatibility'
import { makeStyles, Theme } from '@material-ui/core/styles'
import styles from '../../../styles'
import useTheme from '@material-ui/core/styles/useTheme'
import { ResponsiveContext } from '../../../utils/context/responsiveContext'
import { WHITE } from '../../../theme'
import { formulaireChangementAppartementEnum, profilLocataireCRMEnum } from '../enums/formulaireChangementAppartementEnum'
import SvgAlone from '../../../icons/SvgAlone'
import SvgCouple from '../../../icons/SvgCouple'
import SvgFamily from '../../../icons/SvgFamily'
import SvgOther from '../../../icons/SvgOther'

const useStyles = makeStyles<Theme, { isLargeScreen: boolean }>(
	(theme) => (
		{
			...styles(theme),
			titleStatus: {
				margin: '25px 0 8px'
			},
			titleContainer: {
				marginBottom: 25,
				marginTop: 15
			},
			subTitleStatus: {
				marginBottom: 'auto'
			},
			statusContainer: {
				paddingRight: props => props.isLargeScreen ? 'auto' : 0,
				margin: props => props.isLargeScreen ? 'inherit' : '-8px auto'
			},
			statusElement: {
				color: theme.palette.blue.main,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-evenly',
				alignItems: 'center',
				textAlign: 'center',
				padding: props => props.isLargeScreen ? 30 : 10,
				height: 170,
				width: props => props.isLargeScreen ? 130 : '41%',
				border: `1px solid ${theme.palette.blue.main}`,
				cursor: 'pointer',
				marginBottom: 15,

				'&:nth-child(odd)': {
					marginRight: props => props.isLargeScreen ? 65 : 10
				},

				'&:nth-child(even)': {
					marginRight: props => props.isLargeScreen ? 65 : 0
				},

				'&:last-child': {
					marginRight: props => props.isLargeScreen ? 0 : 'inherit'
				}
			},
			selectedStatusElement: {
				backgroundColor: theme.palette.blue.main,
				'& span': {
					color: WHITE
				}
			},
			buttonHover: {
				'&:hover': {
					backgroundColor: `${WHITE} !important`
				}
			}
		}
	)
)

interface AcceptingProps {
	form: FormApi,
	concerneAppartement: boolean
}

type ProfilLocataireProps =
	AcceptingProps
	& FieldRenderProps<profilLocataireCRMEnum>

const ProfilLocataire: React.FC<ProfilLocataireProps> = (
	{
		form,
		input,
		concerneAppartement
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyles({ isLargeScreen })
	// const intl = useIntl()
	const theme = useTheme()

	const titleStatus: ClassValue = classNames(
		classes.titleStatus,
		classes.text30
	)

	const subTitleStatus: ClassValue = classNames(
		classes.subTitleStatus,
		classes.text3
	)

	const renderIcon = (param: string, selected: boolean) => {
		const color = selected ? WHITE : theme.palette.blue.main

		switch (profilLocataireCRMEnum[param as keyof typeof profilLocataireCRMEnum]) {
			case profilLocataireCRMEnum.alone:
				return <SvgAlone color={color} />
			case profilLocataireCRMEnum.couple:
				return <SvgCouple color={color} />
			case profilLocataireCRMEnum.family:
				return <SvgFamily color={color} />
			case profilLocataireCRMEnum.other:
				return <SvgOther color={color} />
			default:
				return <></>
		}
	}

	return (
		<Grid container>
			<Grid item xs={12} className={classes.titleContainer}>
				<span className={classNames(classes.title6, classes.colorBlue)}>
					<FormattedMessage
						id={`changementAppartement.formulaire.profilLocataire.titre.${concerneAppartement ? 'appartement' : 'parking'}`}
						defaultMessage="Qui habitera dans le logement ?"
						description="Titre du bloc du choix du profil locataire"
					/>
				</span>
			</Grid>

			<Grid container item xs={12} className={classes.statusContainer}>
				<Field
					name={input.name}
					render={
						() => (
							objectGetKeys(profilLocataireCRMEnum)
								.filter((profilLocataire) => isNaN(Number(profilLocataire)))
								.map(
									(profilLocataire) => {
										if (!concerneAppartement && (profilLocataireCRMEnum[profilLocataire as keyof typeof profilLocataireCRMEnum] === profilLocataireCRMEnum.family || profilLocataireCRMEnum[profilLocataire as keyof typeof profilLocataireCRMEnum] === profilLocataireCRMEnum.other)) {
											return <React.Fragment key={profilLocataire} />
										}

										return (
											<div
												onClick={() => {
													form.change(input.name, profilLocataireCRMEnum[profilLocataire as keyof typeof profilLocataireCRMEnum])
													form.change(formulaireChangementAppartementEnum.selectionCopreneur, [])
												}}
												className={
													classNames(
														classes.statusElement,
														profilLocataireCRMEnum[profilLocataire as keyof typeof profilLocataireCRMEnum] === input.value ? classes.selectedStatusElement : classes.buttonHover
													)
												}
												key={profilLocataire}
											>
												{renderIcon(profilLocataire, profilLocataireCRMEnum[profilLocataire as keyof typeof profilLocataireCRMEnum] === input.value)}

												<span className={titleStatus}>
													<FormattedMessage
														id={`changementAppartement.formulaire.profilLocataire.statut.${profilLocataire}`}
														defaultMessage="Vous êtes seul(e)"
														description="who's living appartment"
													/>
												</span>

												<span className={subTitleStatus}>
													<FormattedMessage
														id={`changementAppartement.formulaire.profilLocataire.sousStatut.${profilLocataire}.${concerneAppartement ? 'appartement' : 'parking'}`}
														defaultMessage="Vous êtes la seule personne à signer le bail"
														description="who's living appartment"
													/>
												</span>
											</div>
										)
									}
								)
						)
					}
				/>
			</Grid>
		</Grid>
	)
}

export default ProfilLocataire
